import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { sanitizeEmailHtml } from "../lib/sanitizeHtml";
import {
  Megaphone, Plus, ChevronRight, ChevronLeft, MailOpen, MousePointerClick,
  Reply as ReplyIcon, Eye, Clock, Sparkles,
  CheckCircle2, Send, Users, Loader2, Trash2, FlaskConical,
} from "lucide-react";
import { Avatar, Badge, Button, EmptyState, Input, Skeleton, Tooltip, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatRelative, formatDateTime } from "@/lib/format";
import { emailDomainOf, formatSender, useEmailSending } from "@/lib/email-sending";
import { cn } from "@/lib/utils";
import { NewCampaignDialog } from "@/features/campaigns/NewCampaignDialog";

interface Campaign {
  id: string;
  name: string;
  status: string;
  subject: string;
  body: string | null;
  html_body: string | null;
  from_name: string | null;
  from_email: string | null;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  replied_count: number;
  bounced_count: number;
  unsubscribed_count: number;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
}

interface Recipient {
  id: string;
  contact_id: string;
  status: string;
  sent_at: string | null;
  first_opened_at: string | null;
  open_count: number;
  first_clicked_at: string | null;
  click_count: number;
  replied_at: string | null;
  bounced_at: string | null;
  bounce_reason: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_avatar: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  draft: "zinc",
  scheduled: "amber",
  sending: "blue",
  sent: "emerald",
  paused: "rose",
};

export function CampaignsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id?: string }>();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCampaignOpen, setNewCampaignOpen] = useState(false);

  useEffect(() => {
    if (routeId) return;
    setLoading(true);
    api.get<{ campaigns: Campaign[] }>("/email/campaigns")
      .then((d) => setCampaigns(d.campaigns))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load campaigns"))
      .finally(() => setLoading(false));
  }, [routeId]);

  if (routeId) {
    return <CampaignDetail id={routeId} />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-linear-to-br from-rose-500 to-fuchsia-600 grid place-items-center shadow-lg shadow-rose-500/20">
          <Megaphone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-zinc-100">Campaigns</h1>
          <p className="text-xs text-zinc-500">Mass-email blasts tracked per recipient.</p>
        </div>
        <Button className="ml-auto bg-linear-to-br from-rose-500 to-rose-600 border-0" onClick={() => setNewCampaignOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> New Campaign
        </Button>
      </div>

      {/* Stats summary */}
      <CampaignsSummary campaigns={campaigns} />

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyState icon={Megaphone} title="No campaigns yet" description="Create your first email blast to a smart list." />
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => <CampaignRow key={c.id} campaign={c} />)}
        </div>
      )}

      <NewCampaignDialog
        open={newCampaignOpen}
        onClose={() => setNewCampaignOpen(false)}
        onCreated={(campaignId) => {
          setNewCampaignOpen(false);
          navigate(`/campaigns/${campaignId}`);
        }}
      />
    </div>
  );
}

function CampaignsSummary({ campaigns }: { campaigns: Campaign[] }) {
  const sent = campaigns.filter((c) => c.status === "sent");
  const totals = sent.reduce(
    (acc, c) => ({
      sent: acc.sent + c.sent_count,
      opened: acc.opened + c.opened_count,
      clicked: acc.clicked + c.clicked_count,
      replied: acc.replied + c.replied_count,
    }),
    { sent: 0, opened: 0, clicked: 0, replied: 0 }
  );
  const openRate = totals.sent ? Math.round((totals.opened / totals.sent) * 100) : 0;
  const clickRate = totals.sent ? Math.round((totals.clicked / totals.sent) * 100) : 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <StatTile icon={Send} label="Sent" value={totals.sent.toLocaleString()} accent="indigo" />
      <StatTile icon={MailOpen} label="Open rate" value={`${openRate}%`} sub={`${totals.opened.toLocaleString()} opens`} accent="emerald" />
      <StatTile icon={MousePointerClick} label="Click rate" value={`${clickRate}%`} sub={`${totals.clicked.toLocaleString()} clicks`} accent="violet" />
      <StatTile icon={ReplyIcon} label="Replies" value={totals.replied.toLocaleString()} accent="amber" />
    </div>
  );
}

function StatTile({
  icon: Icon, label, value, sub, accent,
}: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string; accent: string }) {
  const cls: Record<string, string> = {
    indigo: "from-rose-500/20 to-rose-500/0 text-rose-300",
    emerald: "from-emerald-500/20 to-emerald-500/0 text-emerald-300",
    violet: "from-rose-500/20 to-rose-500/0 text-rose-300",
    amber: "from-amber-500/20 to-amber-500/0 text-amber-300",
  };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 flex items-center gap-3">
      <div className={cn("h-9 w-9 rounded-lg bg-linear-to-br grid place-items-center", cls[accent])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="text-lg font-bold text-zinc-100">{value}</p>
        {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
      </div>
    </div>
  );
}

function CampaignRow({ campaign }: { campaign: Campaign }) {
  const openRate = campaign.sent_count ? Math.round((campaign.opened_count / campaign.sent_count) * 100) : 0;
  const clickRate = campaign.sent_count ? Math.round((campaign.clicked_count / campaign.sent_count) * 100) : 0;
  return (
    <Link
      to={`/campaigns/${campaign.id}`}
      className="block rounded-xl border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/60 hover:border-zinc-700 px-4 py-3 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-zinc-100 truncate">{campaign.name}</h3>
            <Badge color={STATUS_COLORS[campaign.status] ?? "zinc"} size="xs">{campaign.status}</Badge>
          </div>
          <p className="text-xs text-zinc-400 truncate">{campaign.subject}</p>
          <p className="text-[11px] text-zinc-500 mt-1">
            {campaign.sent_at ? `Sent ${formatRelative(campaign.sent_at)}` :
             campaign.scheduled_at ? `Scheduled ${formatRelative(campaign.scheduled_at)}` :
             `Created ${formatRelative(campaign.created_at)}`}
            {" · "}
            <Users className="inline h-3 w-3 -mt-px" /> {campaign.total_recipients.toLocaleString()} recipients
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-right shrink-0">
          <Metric icon={MailOpen} value={`${openRate}%`} sub="opened" color="emerald" />
          <Metric icon={MousePointerClick} value={`${clickRate}%`} sub="clicked" color="violet" />
          <Metric icon={ReplyIcon} value={campaign.replied_count.toString()} sub="replies" color="amber" />
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-600 mt-1 shrink-0" />
      </div>
    </Link>
  );
}

function Metric({ icon: Icon, value, sub, color }: { icon: React.ComponentType<{ className?: string }>; value: string; sub: string; color: string }) {
  const text: Record<string, string> = {
    emerald: "text-emerald-300",
    violet: "text-rose-300",
    amber: "text-amber-300",
  };
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="flex items-center gap-1">
        <Icon className={cn("h-3 w-3", text[color])} />
        <span className="text-sm font-semibold text-zinc-100">{value}</span>
      </div>
      <span className="text-[10px] text-zinc-500">{sub}</span>
    </div>
  );
}

// ─── Campaign detail page ─────────────────────────────────────────────────

function CampaignDetail({ id }: { id: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [previewIdx, setPreviewIdx] = useState(0);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const d = await api.get<{ campaign: Campaign; recipients: Recipient[] }>(`/email/campaigns/${id}`);
      setCampaign(d.campaign); setRecipients(d.recipients);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load campaign");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setLoading(true); void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-32" /><Skeleton className="h-72" /></div>;
  }
  if (!campaign) return <p className="text-sm text-zinc-500">Campaign not found.</p>;

  const filtered = statusFilter === "all" ? recipients : recipients.filter((r) => r.status === statusFilter);

  const openRate = campaign.sent_count ? Math.round((campaign.opened_count / campaign.sent_count) * 100) : 0;
  const clickRate = campaign.sent_count ? Math.round((campaign.clicked_count / campaign.sent_count) * 100) : 0;
  const replyRate = campaign.sent_count ? Math.round((campaign.replied_count / campaign.sent_count) * 100) : 0;

  const isDraft = campaign.status === "draft" || campaign.status === "scheduled";
  const previewContact = recipients[previewIdx] ?? null;
  const previewedSubject = renderClientTokens(campaign.subject, previewContact);
  const previewedHtml = renderClientTokens(campaign.html_body ?? "", previewContact);
  const previewedText = renderClientTokens(campaign.body ?? "", previewContact);

  const sendNow = async () => {
    const ok = await confirm({
      title: `Send to ${campaign.total_recipients.toLocaleString()} recipients?`,
      description: "This goes out immediately through your connected ESP. Make sure the preview below looks right.",
      confirmLabel: "Send now",
    });
    if (!ok) return;
    setSending(true);
    try {
      const r = await api.post<{ sent_count: number; bounced_count: number; errors: unknown[] }>(`/email/campaigns/${id}/send`);
      toast.success(`Sent to ${r.sent_count}. ${r.bounced_count > 0 ? `${r.bounced_count} bounced.` : ""}`);
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post(`/email/campaigns/${id}/send-test`);
      toast.success("Test sent to you");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const discard = async () => {
    const ok = await confirm({
      title: "Discard this campaign?",
      description: "Permanently removes the draft and its recipient list. Can't be undone.",
      confirmLabel: "Discard",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/email/campaigns/${id}`);
      toast.success("Campaign discarded");
      navigate("/campaigns");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to discard");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/campaigns" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-zinc-900 text-zinc-400 hover:text-zinc-100">
          <ChevronLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-zinc-100 truncate">{campaign.name}</h1>
            <Badge color={STATUS_COLORS[campaign.status] ?? "zinc"} size="sm">{campaign.status}</Badge>
          </div>
          <p className="text-sm text-zinc-400 truncate">{campaign.subject}</p>
        </div>
      </div>

      {/* ─── Cockpit: actions for a draft, stats for a sent campaign ─── */}
      {isDraft ? (
        <div className="rounded-xl border border-rose-500/30 bg-linear-to-br from-rose-500/8 to-rose-500/5 p-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-linear-to-br from-rose-500 to-rose-600 grid place-items-center shrink-0 shadow-lg shadow-rose-500/30">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="text-sm text-zinc-200 leading-relaxed flex-1">
              <p className="font-semibold text-zinc-100">Ready to send</p>
              <p className="mt-1 text-zinc-400 text-xs">
                {campaign.total_recipients.toLocaleString()} {campaign.total_recipients === 1 ? "recipient" : "recipients"} will receive this email.
                Personalization tokens like <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{first_name}}"}</code>, <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{company}}"}</code>, or any custom field via <code className="text-zinc-300 bg-zinc-900 px-1 rounded">{"{{custom.field_name}}"}</code> are rendered per-recipient at send time.
                Send a test to yourself first.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Tooltip content={`Real send to ${campaign.total_recipients.toLocaleString()} recipients via your ESP`}>
              <Button
                variant="primary"
                onClick={() => void sendNow()}
                disabled={sending || testing || campaign.total_recipients === 0}
                className="bg-linear-to-br from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 border-0"
              >
                {sending
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending…</>
                  : <><Send className="h-3.5 w-3.5 mr-1.5" />Send now</>}
              </Button>
            </Tooltip>
            <Tooltip content="Send one copy to yourself with personalization for the first recipient">
              <Button
                variant="secondary"
                onClick={() => void sendTest()}
                disabled={sending || testing}
              >
                {testing
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending test…</>
                  : <><FlaskConical className="h-3.5 w-3.5 mr-1.5" />Send test to me</>}
              </Button>
            </Tooltip>
            <span className="text-[10px] text-zinc-500 ml-auto">
              Have your AI edit the draft? Just ask in the right panel.
            </span>
            <Tooltip content="Discard this campaign permanently">
              <button
                onClick={() => void discard()}
                className="h-9 w-9 grid place-items-center text-zinc-500 hover:text-rose-300 hover:bg-zinc-900 rounded-lg"
                aria-label="Discard"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>
      ) : (
        <>
          {/* Big stats — only for sent campaigns */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <BigStat label="Recipients" value={campaign.total_recipients.toLocaleString()} icon={Users} color="zinc" />
            <BigStat label="Delivered" value={campaign.delivered_count.toLocaleString()} icon={CheckCircle2} color="emerald" sub={`${campaign.bounced_count} bounced`} />
            <BigStat label="Opened" value={`${openRate}%`} icon={MailOpen} color="indigo" sub={`${campaign.opened_count.toLocaleString()} opens`} />
            <BigStat label="Clicked" value={`${clickRate}%`} icon={MousePointerClick} color="violet" sub={`${campaign.clicked_count.toLocaleString()} clicks`} />
            <BigStat label="Replied" value={`${replyRate}%`} icon={ReplyIcon} color="amber" sub={`${campaign.replied_count.toLocaleString()} replies`} />
          </div>

          {/* AI take */}
          <div className="rounded-xl border border-rose-500/30 bg-linear-to-br from-rose-500/10 to-rose-500/5 p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-linear-to-br from-rose-500 to-rose-600 grid place-items-center shrink-0 shadow-lg shadow-rose-500/30">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="text-sm text-zinc-200 leading-relaxed">
              <p className="font-semibold text-zinc-100">AI take on this campaign</p>
              <p className="mt-1 text-zinc-300">{buildCampaignAiTake(campaign)}</p>
            </div>
          </div>
        </>
      )}

      {/* ─── Email body preview ─── */}
      {(campaign.html_body || campaign.body) && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-100">Preview</h3>
            {previewContact && recipients.length > 1 && (
              <select
                value={previewIdx}
                onChange={(e) => setPreviewIdx(Number(e.target.value))}
                className="ml-auto h-7 rounded-md bg-zinc-900 border border-zinc-800 text-[11px] text-zinc-300 px-1.5 max-w-[200px]"
              >
                {recipients.slice(0, 20).map((r, i) => (
                  <option key={r.id} value={i}>
                    Preview as {r.contact_name ?? r.contact_email ?? "recipient"}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="p-4 bg-zinc-950 border-b border-zinc-900">
            <CampaignSender campaign={campaign} onSaved={(c) => setCampaign(c)} />
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3 mb-1">Subject</p>
            <p className="text-sm font-semibold text-zinc-100">{previewedSubject}</p>
          </div>
          {/* Deliberately white in BOTH themes — it simulates the reader's
              inbox. Text must be a literal dark (zinc-900 remaps in light). */}
          <div className="p-5 bg-white text-[#18181b] max-h-[480px] overflow-y-auto">
            {campaign.html_body ? (
              <div
                className="prose prose-sm max-w-none [&_a]:text-rose-600 [&_p]:my-2 [&_img]:rounded-lg [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(previewedHtml) }}
              />
            ) : (
              <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{previewedText}</pre>
            )}
          </div>
        </div>
      )}

      {/* Recipients table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-zinc-100">Recipients ({recipients.length})</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="ml-auto h-7 rounded-md bg-zinc-900 border border-zinc-800 text-[11px] text-zinc-300 px-1.5"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="replied">Replied</option>
            <option value="bounced">Bounced</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="sent">Sent (not opened)</option>
          </select>
        </div>
        <div className="divide-y divide-zinc-900">
          {filtered.map((r) => <RecipientRow key={r.id} recipient={r} />)}
          {filtered.length === 0 && <p className="p-6 text-center text-xs text-zinc-500">No recipients match this filter.</p>}
        </div>
      </div>
    </div>
  );
}

/** Client-side token renderer for the preview pane. Mirrors the backend
 *  renderer in routes/email.ts so what the user sees IS what the
 *  recipient gets. Keep them in sync if either changes. */
/**
 * The campaign's From, editable until the send starts.
 *
 * Campaigns inherit the workspace sender at creation, which is right by
 * default and wrong for anyone whose marketing identity differs from their
 * transactional one — the whole reason to keep a separate sending domain.
 * Editing here was previously impossible: the API accepted from_name /
 * from_email but nothing in the UI could set them.
 *
 * Reuses the shared sending config (same source as the inbox composer and
 * Settings) so the verified-domain rules are stated once, not restated.
 */
function CampaignSender({ campaign, onSaved }: { campaign: Campaign; onSaved: (c: Campaign) => void }) {
  const toast = useToast();
  const { config } = useEmailSending();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(campaign.from_name ?? "");
  const [email, setEmail] = useState(campaign.from_email ?? "");
  const [saving, setSaving] = useState(false);

  // Locked once the send begins — the stored sender is the historical
  // record of what recipients actually received.
  const locked = campaign.status !== "draft" && campaign.status !== "scheduled";

  const typedDomain = emailDomainOf(email.trim());
  const domainOk = !config?.known || !typedDomain || config.verified_domains.includes(typedDomain);

  // What a send would actually use right now. A blank field falls back to
  // the workspace sender rather than sending blank.
  const shown = formatSender(
    campaign.from_name ?? config?.effective.from_name ?? null,
    campaign.from_email ?? config?.effective.from ?? null,
  );

  const save = async () => {
    setSaving(true);
    try {
      const d = await api.patch<{ campaign: Campaign }>(`/email/campaigns/${campaign.id}`, {
        from_name: name.trim() || null,
        from_email: email.trim() || null,
      });
      onSaved(d.campaign);
      setEditing(false);
      toast.success("Sender updated");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the sender");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">From</p>
          {!locked && (
            <button
              type="button"
              onClick={() => { setName(campaign.from_name ?? ""); setEmail(campaign.from_email ?? ""); setEditing(true); }}
              className="text-[10px] font-medium text-rose-300 hover:text-rose-200"
            >
              Change
            </button>
          )}
        </div>
        <p className="text-sm text-zinc-200">
          {shown || <span className="text-zinc-500 italic">Using the workspace default sender</span>}
        </p>
        {!campaign.from_email && shown && (
          <p className="text-[10px] text-zinc-500 mt-0.5">Workspace default — change it to send this campaign from a different address.</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">From</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your business name" className="h-8 text-xs" maxLength={128} />
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={config?.effective.from ?? "hello@yourdomain.com"} className="h-8 text-xs" />
      </div>
      {!domainOk && (
        <p className="mt-1 text-[11px] text-amber-400">
          {typedDomain} isn't a verified sending domain — this campaign would fail to send.
        </p>
      )}
      {config?.known && config.verified_domains.length > 0 && (
        <p className="mt-1 text-[10px] text-zinc-500">
          Verified: {config.verified_domains.join(", ")} · leave blank to use the workspace default
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={save} loading={saving}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function renderClientTokens(input: string, recipient: Recipient | null): string {
  if (!input) return "";
  const fullName = recipient?.contact_name ?? "";
  const first = fullName ? fullName.split(/\s+/)[0]! : "there";
  const last = fullName.split(/\s+/).slice(1).join(" ") || "";
  return input
    .replace(/\{\{\s*first_name\s*\}\}/gi, first)
    .replace(/\{\{\s*last_name\s*\}\}/gi, last)
    .replace(/\{\{\s*full_name\s*\}\}/gi, fullName || (recipient?.contact_email ?? "there"))
    .replace(/\{\{\s*email\s*\}\}/gi, recipient?.contact_email ?? "")
    .replace(/\{\{\s*company\s*\}\}/gi, "")
    // {{custom.<key>}} — the real value is merged per-recipient at send time;
    // the preview can't know it, so show a readable placeholder rather than
    // leaving the raw token visible.
    .replace(/\{\{\s*custom\.([a-z0-9_]+)\s*\}\}/gi, (_m: string, key: string) => `[${key}]`);
}

function BigStat({ label, value, icon: Icon, color, sub }: { label: string; value: string; icon: React.ComponentType<{ className?: string }>; color: string; sub?: string }) {
  const bg: Record<string, string> = {
    zinc: "bg-zinc-800/40",
    emerald: "bg-emerald-500/10",
    indigo: "bg-rose-500/10",
    violet: "bg-rose-500/10",
    amber: "bg-amber-500/10",
  };
  const text: Record<string, string> = {
    zinc: "text-zinc-300",
    emerald: "text-emerald-300",
    indigo: "text-rose-300",
    violet: "text-rose-300",
    amber: "text-amber-300",
  };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("h-7 w-7 rounded-md grid place-items-center", bg[color])}>
          <Icon className={cn("h-3.5 w-3.5", text[color])} />
        </div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-zinc-100">{value}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function RecipientRow({ recipient }: { recipient: Recipient }) {
  const statusIcon = () => {
    if (recipient.bounced_at) return <Badge color="rose" size="xs">Bounced</Badge>;
    if (recipient.replied_at) return <Badge color="amber" size="xs">Replied</Badge>;
    if (recipient.first_clicked_at) return <Badge color="violet" size="xs">Clicked</Badge>;
    if (recipient.first_opened_at) return <Badge color="emerald" size="xs">Opened</Badge>;
    if (recipient.sent_at) return <Badge color="zinc" size="xs">Sent</Badge>;
    return <Badge color="zinc" size="xs">{recipient.status}</Badge>;
  };

  return (
    <Link
      to={recipient.contact_id ? `/contacts/${recipient.contact_id}` : "#"}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-900/40"
    >
      <Avatar name={recipient.contact_name} email={recipient.contact_email} src={recipient.contact_avatar} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-zinc-100 truncate">{recipient.contact_name ?? recipient.contact_email}</p>
        <p className="text-[11px] text-zinc-500 truncate">{recipient.contact_email}</p>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-[11px] text-zinc-500">
        {recipient.open_count > 0 && <span title="Opens"><Eye className="inline h-3 w-3" /> {recipient.open_count}</span>}
        {recipient.click_count > 0 && <span title="Clicks"><MousePointerClick className="inline h-3 w-3" /> {recipient.click_count}</span>}
        {recipient.sent_at && <span>{formatRelative(recipient.sent_at)}</span>}
      </div>
      {statusIcon()}
    </Link>
  );
}

function buildCampaignAiTake(c: Campaign): string {
  const openRate = c.sent_count ? (c.opened_count / c.sent_count) * 100 : 0;
  const clickRate = c.sent_count ? (c.clicked_count / c.sent_count) * 100 : 0;
  const replyRate = c.sent_count ? (c.replied_count / c.sent_count) * 100 : 0;
  const bounceRate = c.sent_count ? (c.bounced_count / c.sent_count) * 100 : 0;

  const parts: string[] = [];
  if (openRate >= 30) parts.push(`Open rate of ${openRate.toFixed(1)}% is excellent — well above the 21% average for B2B email.`);
  else if (openRate >= 18) parts.push(`Open rate of ${openRate.toFixed(1)}% is solid. Subject line is working.`);
  else parts.push(`Open rate of ${openRate.toFixed(1)}% is below average. Consider testing a different subject line.`);

  if (clickRate >= 5) parts.push(`Click rate of ${clickRate.toFixed(1)}% suggests the body is converting.`);
  else if (clickRate > 0) parts.push(`Click rate is ${clickRate.toFixed(1)}% — try a stronger CTA.`);

  if (replyRate >= 2) parts.push(`Reply rate of ${replyRate.toFixed(1)}% is notable — ${c.replied_count} people are conversations worth following up on personally.`);

  if (bounceRate > 5) parts.push(`Bounce rate of ${bounceRate.toFixed(1)}% is high. Clean the list before the next send.`);

  return parts.join(" ");
}
